// Entry point — full pipeline wiring.
//
// - Mounts canvas + status DOM (kept for the smoke test + dev triage).
// - Parses the App Clip invocation from the page URL.
// - Constructs AssemblerService + ModelDownloader + WheelLoader +
//   WebXrWheelRenderer (with WebXRDriver + GLTFLoader parser) +
//   WheelPlacementController.
// - Mounts the React overlay tree (TopControlsView + ManualAdjustmentPanel).
// - Routes canvas taps to controller.handleTap.
//
// The WebXR session + camera prompt are deferred to a user-tap on the
// StartAROverlay so browsers honour the gesture requirement AND the
// headless smoke test stays green.

import { createRoot } from 'react-dom/client'
import { createElement } from 'react'
import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { estimatedSizeSnapRange } from '@/models/estimated-size-snap-range'
import { IosPlacementController } from '@/controllers/ios-placement-controller'
import { trackPinch, type TPinchTrack } from '@/ar/pinch-tracker'
import { resolveUseIosUi } from '@/models/use-ios-ui-flag'

// WebXR driver doesn't read window.THREE; only the legacy XR8 path did.
// Left here as a no-op safety net in case anything else expects the
// global — harmless under WebXR.
;(window as unknown as { THREE: typeof THREE }).THREE = THREE

import { App } from '@/app'
import { atoLog } from '@/services/ato-log'
import { parseInvocation } from '@/models/app-clip-invocation'
import { APP_ENVIRONMENT_DEFAULT } from '@/models/app-environment'
import { AssemblerService } from '@/services/assembler-service'
import { ModelDownloader } from '@/services/model-downloader'
import { WheelLoader } from '@/services/wheel-loader'
import { WheelPlacementController } from '@/controllers/wheel-placement-controller'
import {
  WebXrWheelRenderer,
  type GltfParser,
} from '@/ar/webxr-wheel-renderer'
import { WebXRDriver } from '@/ar/webxr-driver'
import { PlaneVisualizer } from '@/ar/plane-visualizer'
import { DepthDeltaVisualizer } from '@/ar/depth-delta-visualizer'
import { applyCanvasTouchPolicy } from '@/ar/canvas-touch-policy'
import { createWheelAdjustModeStore } from '@/models/wheel-adjust-mode'
import {
  captureArFrame,
  captureFileName,
  captureMessage,
  saveCapture,
} from '@/ar/capture-ar-frame'
import {
  initialGestureState,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  metersPerPixel,
  screenDragToOffsets,
  screenToRenderedQuadrant,
  rotateScreenDelta,
  renderedViewportHeightPx,
} from '@/ar/touch-gestures'
import { readDomAngle } from '@/lib/readDomAngle'
// PostHog off (2026-10-21, developer) — commented out to restore later, not deleted.
// import { createPostHogClient } from '@/services/posthog-client'
import { configureMessenger, send as sendMessage } from '@/services/messenger-client'
import { createSplashStore } from '@/models/splash-store'
import { resolveSplashMode } from '@/models/ar-splash-mode'
import { parseWheelIdParam } from '@/models/wheel-id-param'
import { fetchWheelBrand } from '@/services/fetch-wheel-brand'
import { resolvePdpReferrer } from '@/services/pdp-referrer'
import type { WheelDetails } from '@/models/wheel-details'
import {
  buildSessionStartFailedMessage,
  buildSessionStartMessage,
  buildSessionSummaryMessage,
  buildTrackingDegradedMessage,
  buildWheelSizeMissingMessage,
} from '@/services/session-report'
import { createSustainedDegradedDetector, SUSTAINED_DEGRADED_MS } from '@/services/sustained-degraded'
import {
  buildCapabilityProbe,
  buildSessionStartProperties,
  buildSessionSummaryProperties,
  buildSuperProperties,
  buildTrackingDegradedProperties,
  trackingHealthFor,
} from '@/services/ar-diagnostics'
import { AnchoredWallGridVisualizer } from '@/ar/anchored-wall-grid-visualizer'
import { PlaneDiagnosticsHUD } from '@/components/plane-diagnostics-hud/plane-diagnostics-hud'
import { DepthReadout } from '@/components/depth-readout/depth-readout'
import { TrackModeToggle } from '@/components/track-mode-toggle/track-mode-toggle'
import { PreviewModeToggle } from '@/components/track-mode-toggle/preview-mode-toggle'

const STATES = {
  BOOTING: 'booting',
  AR_LOADING: 'ar-loading',
  AR_READY: 'ar-ready',
  AR_ERROR: 'ar-error',
} as const

void bootstrap()

async function bootstrap(): Promise<void> {
  const app = document.getElementById('app')
  if (app == null) throw new Error('expected #app in index.html')

  const { canvas, status } = mountChrome(app)

  // Telemetry first, so the capability probe below (and any boot
  // failure after it) actually reaches PostHog. posthog-js has been a
  // dependency since this repo was created and was never wired, so
  // every atoLog.event() until now fired into a null client and died in
  // the console (2026-08-24).
  // PostHog is OFF (2026-10-21, developer): atoLog runs with no client —
  // console mirror only, a supported state. Slack session reports go through
  // the messenger and are unaffected. To restore, uncomment this block and
  // the import above:
  //
  // atoLog.configure({
  //   client: createPostHogClient({
  //     token: import.meta.env.VITE_POSTHOG_KEY,
  //     host: import.meta.env.VITE_POSTHOG_HOST,
  //   }),
  // })
  atoLog.register(buildSuperProperties({ hostname: window.location.hostname }))
  installGlobalErrorReporting()
  // Held so the session reports can name the device without re-probing at
  // teardown. Populated asynchronously; the reports tolerate an empty bag.
  let capabilityBag: Record<string, unknown> = {}
  atoLog.debug('main.ts mounted; preparing WebXR driver')
  // The splash (2026-10-13): what to offer, from this same probe, and the
  // wheel's brand by the launch URL's wheel id.
  const splashStore = createSplashStore()
  void reportDeviceCapabilities().then((bag) => {
    capabilityBag = bag
    splashStore.setMode(
      resolveSplashMode({
        immersiveArSupported: bag.immersive_ar_supported === true,
        userAgent: navigator.userAgent,
      }),
    )
  })
  status.dataset.state = STATES.AR_LOADING

  // WebXR availability is checked when the user taps Start AR (the
  // driver throws if navigator.xr is unavailable or immersive-ar
  // isn't supported). The driver swap from XR8 → WebXR removed the
  // CDN-script bootstrap dance.

  const invocation = readInvocation()
  // ADR-086 — point the messenger at this environment's parts worker. The
  // invocation's `env` is the same one the assembler poll uses, so a session
  // cannot report to staging while loading from production.
  configureMessenger(invocation?.env ?? APP_ENVIRONMENT_DEFAULT)
  const splashWheelId = parseWheelIdParam(new URL(window.location.href).searchParams.get('wheel'))
  if (splashWheelId != null) {
    void fetchWheelBrand({ env: invocation?.env ?? APP_ENVIRONMENT_DEFAULT, wheelId: splashWheelId }).then((brand) =>
      splashStore.setBrand(brand),
    )
  }
  // Correlates the start and summary posts for one session.
  const sessionId = newSessionId()
  // Whose storefront, which page — resolved once at boot because the
  // referrer and ancestor chain never change after navigation. Null on a
  // QR scan; the reports omit the field then.
  const pdpReferrer = resolvePdpReferrer({
    returnUrl: invocation?.returnURL?.toString() ?? null,
    clientHostUrl: invocation?.clientHostURL ?? null,
    referrer: document.referrer,
    ancestorOrigins: Array.from(location.ancestorOrigins ?? []),
  })
  // Latest resolved wheel — read at report time, because details can land
  // after Start AR is tapped and the summary fires at teardown.
  let latestWheelDetails: WheelDetails | null = null
  // Debug toggles via URL query — set ?cameraLock=1 to parent the wheel
  // directly to the camera at a fixed local offset. Wheel becomes screen-
  // locked; if it still jitters with this on, the renderer/GLB is the
  // source, not SLAM.
  const debugFlags = readDebugFlags()
  // Depth placement is the DEFAULT since the 2026-08-21 daylight car
  // validation (Δ within ±3cm). ?planePlace is the legacy escape hatch
  // back to plane-hit-only placement; devices whose ARCore lacks the
  // Depth API degrade to the plane path automatically (the policy falls
  // back when no depth sample ever arrives).
  const depthPlacement = !debugFlags.planePlace
  // The session's diagnostics bag, read at the moment of asking — by the
  // end-of-session event and by the live degraded report (2026-10-21).
  const sessionSummaryProps = (): Record<string, unknown> => {
    const planeCount = driver.detectedPlanes.getState().length
    return buildSessionSummaryProperties(driver.sessionInfo, {
      wheelsPlaced: controller.getState().placedWheelCount,
      floorFound: renderer._floorY() != null,
      planeCount,
      trackingHealth: trackingHealthFor(driver.sessionInfo, planeCount),
    })
  }
  // Photos saved this session — the end report says whether one was taken.
  let photosCaptured = 0
  const driver = new WebXRDriver({
    enableDepthSensing: depthPlacement,
    // ?surfaceYaw needs the off-center depth samples; two extra CPU
    // pixel reads on the existing cadence, only while the flag is on.
    sampleDepthNeighborhood: debugFlags.surfaceYaw,
    // Emitted here rather than inside the driver because the summary
    // also needs renderer + controller state (floor found, wheels
    // placed) that the driver has no reference to.
    onSessionEnded: () => {
      const summaryProps = sessionSummaryProps()
      atoLog.event('ar_session_summary', summaryProps)
      sendMessage(
        buildSessionSummaryMessage({
          sessionId,
          environment: invocation?.env ?? APP_ENVIRONMENT_DEFAULT,
          capability: capabilityBag,
          summary: summaryProps,
          photosCaptured,
          details: latestWheelDetails,
          pdpReferrer,
        }),
      )
    },
  })
  // Touch is the DEFAULT adjust mode now; ?arrowUi is the escape hatch
  // back to the joystick pad for anyone who wants discrete nudges. The
  // gear popover writes this at runtime.
  const wheelAdjustMode = createWheelAdjustModeStore(
    debugFlags.arrowUi ? 'arrows' : 'touch',
  )
  const isTouchMode = () => wheelAdjustMode.get() === 'touch'

  const gltfParser: GltfParser = parseGlbBytes
  const renderer = new WebXrWheelRenderer({
    driver,
    gltfParser,
    canvas,
    // Wireframe hit-indicator grid — same diagnostic family as
    // PlaneVisualizer / AnchoredWallGridVisualizer. Gated on planeMode
    // so the customer-facing view has no green geometry; add
    // ?planeMode=1 to bring it back for debugging.
    wallDebugVisible: debugFlags.planeMode,
    cameraLockMode: debugFlags.cameraLock,
    depthPlaceMode: depthPlacement,
    // Instant placement rides with the depth flow: preview == final
    // pose, so the legacy drop animation only added perceived drift.
    instantPlaceMode: depthPlacement || debugFlags.instantPlace,
    depthPrepassMode: debugFlags.depthPrepass,
    surfaceYawMode: debugFlags.surfaceYaw,
    ringPlaceMode: debugFlags.ringPlace,
    // ?use_ios_ui aims with the 2D framing ring; a 3D preview (and the
    // gauge that follows it) would be a second target.
    aimingPreview: !debugFlags.useIosUi,
    selectionRingStyle: debugFlags.useIosUi ? 'ios' : 'android',
    // ?use_ios_ui: the wheel's centre goes where the ring framed the rim.
    framedPlacement: debugFlags.useIosUi,
  })
  if (!depthPlacement) {
    atoLog.event('plane_place_legacy_mode_enabled')
    // eslint-disable-next-line no-console
    console.log('[ATO] PLANE-PLACE legacy mode — depth placement disabled via ?planePlace')
  }
  if (debugFlags.ringPlace) {
    atoLog.event('ring_place_mode_enabled')
    // eslint-disable-next-line no-console
    console.log('[ATO] RING-PLACE — aim the circle at the rim; the tap drops the wheel centred on it')
  }
  if (debugFlags.cameraLock) {
    atoLog.event('camera_lock_mode_enabled')
    // eslint-disable-next-line no-console
    console.log('[ATO] CAMERA-LOCK debug mode active — wheel will be screen-locked')
  }
  if (debugFlags.groundLine) {
    renderer.setGroundLineMode(true)
    atoLog.event('ground_line_mode_enabled')
    // eslint-disable-next-line no-console
    console.log(
      '[ATO] GROUND-LINE debug mode active — first tap freezes ground Y, subsequent placements share it',
    )
  }

  // Plane-diagnostic mode: visualise every ARCore-detected plane +
  // show HUD. Wired regardless of ?planeMode since it's cheap to keep on
  // while iterating; the wheel UI is still there but hidden by the
  // planeMode gate below.
  let planeVisualizer: PlaneVisualizer | null = null
  let anchoredWallGrid: AnchoredWallGridVisualizer | null = null
  let depthDeltaVisualizer: DepthDeltaVisualizer | null = null

  const loader = new WheelLoader({
    assembler: new AssemblerService(),
    downloader: new ModelDownloader(),
    setWheelModel: (input) => renderer.setWheelModel(input),
    // A payload with no size hides the header's size segment and
    // degrades the pinch to continuous — both silent on the phone, so
    // the catalog gap is posted where a person sees it (2026-09-07).
    onWheelDetails: (details) => {
      latestWheelDetails = details
      const notice = buildWheelSizeMissingMessage({
        details,
        environment: invocation?.env ?? APP_ENVIRONMENT_DEFAULT,
      })
      if (notice != null) sendMessage(notice)
    },
  })

  // Toast bus: controller fires onToast → push to a subscribe-able store
  // the React tree reads via useSyncExternalStore. Toast auto-dismisses
  // via setTimeout in the Toast component, which calls onToastDismissed
  // here to clear the store.
  const toastStore = createToastStore()

  const controller = new WheelPlacementController({
    renderer,
    loader,
    onToast: (message) => {
      atoLog.event('toast_shown', { message })
      toastStore.show(message)
    },
    // Under ?touchUi selection is a visible green ring the user reaches
    // for deliberately; auto-selecting on placement would arm drag and
    // pinch on a wheel nobody has touched.
    autoSelectOnPlace: !isTouchMode(),
    // ?use_estimated_size_snap_range=N — the pinch's span from a size
    // estimated off the GLB (no payload size). Default 3; 0 = no pinch.
    estimatedSizeSnapRangeInches: estimatedSizeSnapRange(
      new URL(window.location.href).searchParams,
    ),
  })

  // One writer, three readers: the ring, placement's auto-select, and
  // the canvas gestures below all follow the store rather than caching
  // a boot-time copy.
  wheelAdjustMode.subscribe(() => {
    controller.setAutoSelectOnPlace(!isTouchMode())
  })

  // ?use_ios_ui — the iOS flow, composed over the controller above. Null
  // flag-off, and every branch below that reads it is skipped then.
  const iosController = debugFlags.useIosUi
    ? new IosPlacementController({
        controller,
        renderer,
        loader,
        onToast: (message) => {
          atoLog.event('toast_shown', { message })
          toastStore.show(message)
        },
        // iOS ticks a UISelectionFeedbackGenerator per committed size.
        onSizeStep: () => navigator.vibrate?.(8),
      })
    : null
  if (iosController != null) {
    atoLog.event('ios_ui_enabled')
    // eslint-disable-next-line no-console
    console.log('[ATO] IOS-UI — framed placement, MASK band, wheel-size menu + pinch')
  }

  if (invocation != null) {
    loader.load(invocation)
  } else {
    atoLog.debug('no invocation on URL — loader is idle until a code/url arrives')
  }

  // Camera button. The passthrough is composited by the system, so a
  // canvas capture would save the wheel on transparency — this pulls the
  // real frame through WebXR raw camera access and paints the scene over
  // it. Every failure path reports to the user rather than throwing.
  const takeScreenshot = (): void => {
    // Deferred into the XR frame loop: the camera texture is only valid
    // inside the rAF callback of the frame it came from, so asking from
    // this click handler always failed.
    driver.requestCapture((context) => {
      void captureArFrame({
        ...context,
        scene: renderer._scene(),
        camera: renderer._camera(),
      }).then((result) => {
        if (result.ok) {
          photosCaptured += 1
          saveCapture(result.blob, captureFileName(new Date()))
        }
        atoLog.event('screenshot_captured', {
          ok: String(result.ok),
          reason: result.ok ? 'none' : result.reason,
        })
        toastStore.show(captureMessage(result))
      })
    })
  }

  /** Rotation between the DOM frame pointer events arrive in and the
   *  device frame the compositor renders in. Derived from the viewer
   *  pose, never from `innerWidth > innerHeight`: the driver's own
   *  frame loop is the only orientation signal that stays live during
   *  an immersive-ar session. */
  const renderedQuadrant = () =>
    screenToRenderedQuadrant({
      viewerRollQuadrant: driver.sessionInfo.viewerRollQuadrant,
      domRotationAngle: readDomAngle(),
    })

  const tapInput = (point: { x: number; y: number }) => {
    const quadrant = renderedQuadrant()
    return {
      screenPoint: point,
      viewSize: { width: window.innerWidth, height: window.innerHeight },
      isLandscape: quadrant === 90 || quadrant === 270,
    }
  }

  {
    // Take touch gestures off the browser first. Without this it treats
    // one finger as a scroll and two as a page zoom, delivering about
    // one pointermove before firing pointercancel — which is why the
    // first LUAT found drag "locked" and pinch limited to one step per
    // gesture.
    applyCanvasTouchPolicy(canvas)

    // ?touchUi — the wheel is the control. Taps still route to
    // handleTap (which already selects a touched wheel and deselects on
    // empty space); drags and pinches feed the same additive controller
    // methods the joystick used, so the two input styles cannot drift
    // apart in what they do to a wheel.
    let gesture = initialGestureState()
    // ?use_ios_ui — iOS reads a pinch cumulatively and has no drag-to-move.
    let iosPinch: TPinchTrack | null = null
    const feedIosPinch = () => {
      if (iosController == null) return
      const next = trackPinch(iosPinch, gesture.pointers)
      iosPinch = next.track
      if (next.event?.kind === 'begin') iosController.pinchBegin(next.event.midpoint)
      else if (next.event?.kind === 'change') iosController.pinchChange(next.event.scale)
      else if (next.event?.kind === 'end') iosController.pinchEnd()
    }

    canvas.addEventListener('pointerdown', (e) => {
      gesture = onPointerDown(gesture, { id: e.pointerId, x: e.clientX, y: e.clientY })
      feedIosPinch()
    })

    canvas.addEventListener('pointermove', (e) => {
      const result = onPointerMove(gesture, {
        id: e.pointerId,
        x: e.clientX,
        y: e.clientY,
      })
      gesture = result.state
      if (iosController != null) {
        feedIosPinch()
        return
      }

      // Arrow mode owns adjustment through the joystick pad; a stray
      // two-finger gesture must not also resize the wheel there.
      if (!isTouchMode()) return

      if (result.pinch != null) {
        // Snapped to whole-inch diameters, 9"–30" (2026-09-07); the
        // header pill reads the same number. A payload without a size
        // falls back to the continuous 0.25–4 behaviour inside.
        controller.adjustScaleSnapped(result.pinch.scaleDelta)
      }

      if (result.drag != null) {
        // Scale the drag by how far away the wheel is, so the same
        // finger travel moves a near wheel less than a far one. No
        // selection (or no usable scale) means nothing to move.
        const distanceM = renderer._selectedWheelDistanceM()
        if (distanceM == null) return
        // Correct the delta into the RENDERED frame before it becomes a
        // world offset. In landscape the DOM frame and the device frame
        // are a quarter turn apart, so an uncorrected delta moved the
        // wheel along the wrong axis AND — because the vertical extent
        // it is scaled against also swaps — by the wrong distance
        // (2026-08-24 LUAT). Quadrant 0 leaves both untouched, so the
        // portrait path is bit-for-bit what it was.
        const quadrant = renderedQuadrant()
        const scale = metersPerPixel({
          distanceM,
          viewportHeightPx: renderedViewportHeightPx({
            domWidth: window.innerWidth,
            domHeight: window.innerHeight,
            quadrant,
          }),
          verticalFovDeg: renderer._camera().fov,
        })
        if (scale <= 0) return
        const rotated = rotateScreenDelta({
          dx: result.drag.dx,
          dy: result.drag.dy,
          quadrant,
        })
        const { horizontal, height } = screenDragToOffsets({
          dx: rotated.dx,
          dy: rotated.dy,
          metersPerPixel: scale,
        })
        controller.adjustHorizontal(horizontal)
        controller.adjustHeight(height)
      }
    })

    const endPointer = (e: PointerEvent) => {
      const result = onPointerUp(gesture, e.pointerId)
      gesture = result.state
      if (iosController != null) {
        feedIosPinch()
        if (result.tap != null) iosController.handleTap(tapInput(result.tap))
        return
      }
      // Only a genuine tap reaches handleTap. A drag that ended over
      // empty space must not deselect the wheel it just moved.
      if (result.tap != null) controller.handleTap(tapInput(result.tap))
    }
    canvas.addEventListener('pointerup', endPointer)
    // A cancelled pointer (system gesture, call overlay) ends the
    // gesture without acting — otherwise the state machine keeps a
    // phantom finger down and the next real touch is read as a pinch.
    canvas.addEventListener('pointercancel', endPointer)
  }

  // (XR8's two-finger recenter escape hatch was removed with the WebXR
  // migration. ARCore handles drift recovery natively via re-localisation
  // and rarely needs manual intervention. If WebXR turns out to exhibit
  // similar divergence, we'll wire requestReferenceSpace + a re-bootstrap
  // helper on WebXRDriver and re-add this handler.)

  const reactMount = document.createElement('div')
  reactMount.id = 'react-overlay'
  reactMount.style.position = 'fixed'
  reactMount.style.inset = '0'
  reactMount.style.pointerEvents = 'none'
  // No text selection / long-press highlight on any overlay UI — the
  // AR canvas is the interaction surface; incidental drags on labels
  // should never trigger selection outlines.
  reactMount.style.userSelect = 'none'
  ;(reactMount.style as unknown as { webkitUserSelect: string }).webkitUserSelect =
    'none'
  ;(
    reactMount.style as unknown as { webkitTouchCallout: string }
  ).webkitTouchCallout = 'none'
  app.appendChild(reactMount)

  createRoot(reactMount).render(
    createElement(App, {
      loader,
      controller,
      renderer,
      toastStore,
      splashStore,
      // Dev-only HUD was an XR8-specific surface (read from
      // RealXR8Driver.diagnostics). WebXR driver doesn't expose an
      // equivalent yet; pass undefined and the HUD doesn't render.
      // TODO: add a WebXR-side diagnostics store with frame-pose +
      // hit-test stats so we have on-screen telemetry under ARCore too.
      diagnosticsStore: undefined,
      getSessionInfo: () => driver.sessionInfo,
      onForceReady: () => driver.forceReady(),
      getTrackMode: debugFlags.trackToggle
        ? () => renderer.getTrackMode()
        : undefined,
      setTrackMode: debugFlags.trackToggle
        ? (m) => renderer.setTrackMode(m)
        : undefined,
      getPreviewMode: debugFlags.trackToggle
        ? () => renderer.getPreviewMode()
        : undefined,
      setPreviewMode: debugFlags.trackToggle
        ? (m) => renderer.setPreviewMode(m)
        : undefined,
      onStartAR: async () => {
        try {
          await renderer.start()
        } catch (error) {
          // The splash shows the error; this makes it visible to us too, with
          // the device named. Rethrown so the splash still gets it.
          sendMessage(
            buildSessionStartFailedMessage({
              sessionId,
              environment: invocation?.env ?? APP_ENVIRONMENT_DEFAULT,
              capability: capabilityBag,
              error,
              details: latestWheelDetails,
              pdpReferrer,
            }),
          )
          throw error
        }
        // enabledFeatures is populated by now: this is the event that
        // separates "ARCore refused plane detection" from "the session
        // never started" for a device that finds nothing.
        const startProps = buildSessionStartProperties(driver.sessionInfo)
        atoLog.event('ar_session_started', startProps)
        sendMessage(
          buildSessionStartMessage({
            sessionId,
            environment: invocation?.env ?? APP_ENVIRONMENT_DEFAULT,
            capability: capabilityBag,
            started: startProps,
            details: latestWheelDetails,
            pdpReferrer,
          }),
        )
        // Tracking-health alarm, fired at most once per session.
        //
        // Its own event rather than a summary field: a user whose
        // wheels are floating away closes the tab, and the session
        // summary may never be sent. This one fires while the session
        // is still live. The plane store notifies on every sessionInfo
        // write, so no extra timer is needed.
        //
        // Slack hears about it only once degraded has HELD for
        // SUSTAINED_DEGRADED_MS of the live session (2026-10-21): a
        // momentary dip — the camera swept off the floor, or pointed at
        // nothing before the X — is not a finding.
        let degradedReported = false
        const sustainedDegraded = createSustainedDegradedDetector(SUSTAINED_DEGRADED_MS)
        driver.detectedPlanes.subscribe(() => {
          const planeCount = driver.detectedPlanes.getState().length
          const isDegraded = trackingHealthFor(driver.sessionInfo, planeCount) === 'degraded'
          if (isDegraded && !degradedReported) {
            degradedReported = true
            atoLog.event(
              'ar_tracking_degraded',
              buildTrackingDegradedProperties(driver.sessionInfo, planeCount),
            )
          }
          if (sustainedDegraded(isDegraded, driver.sessionInfo.elapsedMs)) {
            sendMessage(
              buildTrackingDegradedMessage({
                sessionId,
                environment: invocation?.env ?? APP_ENVIRONMENT_DEFAULT,
                capability: capabilityBag,
                summary: sessionSummaryProps(),
                degradedForS: SUSTAINED_DEGRADED_MS / 1000,
                details: latestWheelDetails,
                pdpReferrer,
              }),
            )
          }
        })
        // Placement gauge — the green depth line on the floor. Product
        // affordance, mounted whenever depth placement is active (the
        // default). Updates ride the plane store's per-frame notify.
        if (depthPlacement && depthDeltaVisualizer == null) {
          depthDeltaVisualizer = new DepthDeltaVisualizer({
            scene: renderer._scene(),
            camera: renderer._camera(),
            // Aim point for the gauge — the renderer's ARBITRATED hit,
            // the same one the tap consumes. Reading the raw depth
            // sample here is what let the line and the wheel disagree
            // in X/Z (2026-08-24, Xiaomi): the depth map overshot the
            // floor by 3m, chooseDepthPlacement correctly discarded it,
            // and the line drew at the discarded point anyway — off
            // toward the horizon while the wheel stayed put.
            getDepthHit: () => renderer._aimHit(),
            // Floor level for the gauge — read through the renderer's
            // sticky estimate, the SAME primitive the wheel placement
            // uses. Reading the raw driver hit here is what let the
            // line and the wheel disagree in Y on a dropout
            // (2026-08-22): the line held its last floor position while
            // the wheel climbed the back plane.
            getFloorY: () => renderer._floorY(),
            // The gauge belongs to the AIMING state: hidden once a
            // wheel is placed, back when the next placement begins.
            getAimingActive: () => renderer._previewActive(),
          })
          driver.detectedPlanes.subscribe(() => {
            depthDeltaVisualizer?.update()
          })
          atoLog.event('depth_delta_visualizer_attached')
        }
        // Plane / anchored-wall-grid visualizers are DEV DIAGNOSTICS.
        // Only attach when ?planeMode=1 is on the URL — otherwise the
        // green wireframes clutter the user-facing AR view.
        if (debugFlags.planeMode) {
          if (planeVisualizer == null) {
            planeVisualizer = new PlaneVisualizer(
              renderer._scene(),
              driver.detectedPlanes,
            )
            atoLog.event('plane_visualizer_attached')
          }
          if (anchoredWallGrid == null) {
            anchoredWallGrid = new AnchoredWallGridVisualizer(
              renderer._scene(),
              driver.detectedPlanes,
              () => driver.sessionInfo,
            )
            atoLog.event('anchored_wall_grid_attached')
          }
        }
      },
      // Wheel-preview + tap-to-place UI is hidden when planeMode is on
      // so we can focus on plane-detection diagnostics without the wheel
      // getting in the way visually.
      hideWheelUI: debugFlags.planeMode,
      useGroundLine: debugFlags.groundLine,
      useUiTest: debugFlags.uiTest,
      wheelAdjustMode,
      onCamera: takeScreenshot,
      showOrientationHud: debugFlags.orHud,
      useIosUi: iosController != null,
      iosController: iosController ?? undefined,
      exit: resolveExit(invocation),
      onRetryLoad: () => {
        if (invocation != null) loader.retry(invocation)
      },
      onCancelLoad: () => loader.cancel(),
      getRenderedQuadrant: renderedQuadrant,
      onClose: () => {
        const returnURL = invocation?.returnURL
        if (returnURL != null) {
          window.location.href = returnURL.toString()
        } else {
          window.history.length > 1
            ? window.history.back()
            : (window.location.href = '/')
        }
      },
    }),
  )

  // Plane HUD lives in its own overlay so it renders regardless of App's
  // state (e.g., before the Start AR button is tapped). Only mounted when
  // ?planeMode=1 is set — no visual clutter for regular users.
  if (debugFlags.planeMode) {
    const planeHudMount = document.createElement('div')
    planeHudMount.id = 'plane-hud-overlay'
    planeHudMount.style.position = 'fixed'
    planeHudMount.style.inset = '0'
    planeHudMount.style.pointerEvents = 'none'
    planeHudMount.style.userSelect = 'none'
    app.appendChild(planeHudMount)
    createRoot(planeHudMount).render(
      createElement(PlaneDiagnosticsHUD, {
        store: driver.detectedPlanes,
        getSessionInfo: () => driver.sessionInfo,
      }),
    )
  }

  // ?depthHud / ?depthPlace — depth-sensing measurement readout. Own
  // overlay (same pattern as the plane HUD) so it renders regardless of
  // App state and stays out of the customer-facing view without a flag.
  if (debugFlags.depthHud) {
    const depthHudMount = document.createElement('div')
    depthHudMount.id = 'depth-hud-overlay'
    depthHudMount.style.position = 'fixed'
    depthHudMount.style.inset = '0'
    depthHudMount.style.pointerEvents = 'none'
    depthHudMount.style.userSelect = 'none'
    app.appendChild(depthHudMount)
    createRoot(depthHudMount).render(
      createElement(DepthReadout, {
        store: driver.detectedPlanes,
        getDepthStatus: () => driver.sessionInfo.depthStatus,
      }),
    )
  }

  // ?trackToggle — mount BOTH debug toggles (track mode + preview
  // mode) so A/B UAT covers all four combinations from a single flag.
  // v2 UI consolidates these into a gear/config popover inside the
  // floating icon row — skip the separate overlays there.
  if (debugFlags.trackToggle && !debugFlags.controlChips) {
    const toggleMount = document.createElement('div')
    toggleMount.id = 'debug-toggle-overlay'
    toggleMount.style.position = 'fixed'
    toggleMount.style.inset = '0'
    toggleMount.style.pointerEvents = 'none'
    toggleMount.style.userSelect = 'none'
    app.appendChild(toggleMount)
    createRoot(toggleMount).render(
      createElement(
        'div',
        null,
        createElement(TrackModeToggle, {
          initialMode: renderer.getTrackMode(),
          onChange: (mode) => renderer.setTrackMode(mode),
        }),
        createElement(PreviewModeToggle, {
          initialMode: renderer.getPreviewMode(),
          onChange: (mode) => renderer.setPreviewMode(mode),
        }),
      ),
    )
  }
}

function mountChrome(app: HTMLElement): {
  canvas: HTMLCanvasElement
  status: HTMLDivElement
} {
  const canvas = document.createElement('canvas')
  canvas.id = 'ar-canvas'
  canvas.style.display = 'block'
  canvas.style.width = '100vw'
  canvas.style.height = '100vh'
  canvas.style.position = 'fixed'
  canvas.style.inset = '0'
  canvas.width = window.innerWidth
  canvas.height = window.innerHeight
  app.appendChild(canvas)

  // Invisible data-state carrier — the smoke test reads
  // #status[data-state]; it has no visual role (never gets text). Kept
  // display:none so it doesn't render as a stray empty pill at the
  // top-left of the overlay.
  const status = document.createElement('div')
  status.id = 'status'
  status.dataset.state = STATES.BOOTING
  status.style.display = 'none'
  app.appendChild(status)

  return { canvas, status }
}

/** ?use_ios_ui — where the iOS UI's ways out go (iOS exitURL): the return
 *  URL, else the client host as given, else nowhere. Web URLs only. */
function resolveExit(
  invocation: ReturnType<typeof parseInvocation>,
): { url: URL; isReturnURL: boolean } | null {
  const returnURL = invocation?.returnURL
  if (returnURL != null && isWebUrl(returnURL)) return { url: returnURL, isReturnURL: true }
  const host = invocation?.clientHostURL
  if (host == null) return null
  try {
    const url = new URL(host)
    return isWebUrl(url) ? { url, isReturnURL: false } : null
  } catch {
    return null
  }
}

function isWebUrl(url: URL): boolean {
  return url.protocol === 'https:' || url.protocol === 'http:'
}

function readInvocation(): ReturnType<typeof parseInvocation> {
  try {
    return parseInvocation(new URL(window.location.href))
  } catch {
    return null
  }
}

function readDebugFlags(): {
  cameraLock: boolean
  planeMode: boolean
  trackToggle: boolean
  controlChips: boolean
  groundLine: boolean
  uiTest: boolean
  orHud: boolean
  depthHud: boolean
  depthPlace: boolean
  instantPlace: boolean
  planePlace: boolean
  /** ?surfaceYaw — placement takes the struck surface's yaw instead of
   *  facing the phone. Flag-off is the shipped view-aligned behaviour. */
  surfaceYaw: boolean
  /** ?ringPlace — two-step placement, OFF unless present (ships dark).
   *  The aiming preview is a plain circle instead of the wheel GLB; the
   *  user puts it around the rim of their real wheel and the tap drops
   *  the wheel centred on it, taking the estimated contact lift out of
   *  the drop entirely. */
  ringPlace: boolean
  arrowUi: boolean
  depthPrepass: boolean
  /** ?use_ios_ui (2026-10-17) — the iOS 0.7.0 UI (framed placement, the
   *  MASK band, the wheel-size menu + pinch). ON by default since
   *  2026-10-19; ?use_ios_ui=0 returns the shipped Android UI. */
  useIosUi: boolean
} {
  try {
    const params = new URL(window.location.href).searchParams
    // Presence-based flags — `?planeMode`, `?planeMode=1`, and
    // `?planeMode=anything` all count as truthy. Bare presence is
    // enough; no explicit `=1` requirement.
    return {
      cameraLock: params.has('cameraLock'),
      planeMode: params.has('planeMode'),
      trackToggle: params.has('trackToggle'),
      controlChips: params.has('controlChips'),
      groundLine: params.has('groundLine'),
      uiTest: params.has('uiTest'),
      orHud: params.has('orHud'),
      // ?depthPlace implies ?depthHud — the depth session request +
      // measurement readout always ride along with the placement change.
      depthHud: params.has('depthHud') || params.has('depthPlace'),
      depthPlace: params.has('depthPlace'),
      instantPlace: params.has('instantPlace'),
      planePlace: params.has('planePlace'),
      surfaceYaw: params.has('surfaceYaw'),
      ringPlace: params.has('ringPlace'),
      // ?arrowUi — back to the joystick pad. Touch is the default now,
      // and the gear popover is the real control; this is the escape
      // hatch for a device where touch misbehaves.
      arrowUi: params.has('arrowUi'),
      // ?depthPrepass — real-geometry occlusion instead of the oversized
      // barrel-mask proxy, so the tire gradient sits behind a nearer
      // wheel like the wheel body does.
      depthPrepass: params.has('depthPrepass'),
      useIosUi: resolveUseIosUi(params),
    }
  } catch {
    return {
      cameraLock: false,
      planeMode: false,
      trackToggle: false,
      controlChips: false,
      groundLine: false,
      uiTest: false,
      orHud: false,
      depthHud: false,
      depthPlace: false,
      instantPlace: false,
      planePlace: false,
      surfaceYaw: false,
      ringPlace: false,
      arrowUi: false,
      depthPrepass: false,
      useIosUi: true,
    }
  }
}

// Function declaration (not const) so it hoists — bootstrap() runs
// synchronously to its first await and references this from line 65.
// As a `const` arrow it was in the temporal dead zone when the previous
// `await waitForXR8()` at the top of bootstrap was removed.
function parseGlbBytes(bytes: ArrayBuffer): Promise<THREE.Group> {
  return new Promise((resolve, reject) => {
    new GLTFLoader().parse(
      bytes,
      '',
      (gltf) => resolve(gltf.scene as unknown as THREE.Group),
      (err) => reject(err as unknown as Error),
    )
  })
}

// Tiny subscribe-able store for the toast bus. Same shape as
// WheelLoader.subscribe/getState so App can read it with
// useSyncExternalStore.
export interface ToastStore {
  show(message: string): void
  dismiss(): void
  getState(): string | null
  subscribe(listener: () => void): () => void
}

function createToastStore(): ToastStore {
  let current: string | null = null
  const listeners = new Set<() => void>()
  const notify = (): void => {
    for (const l of Array.from(listeners)) l()
  }
  return {
    show(message) {
      current = message
      notify()
    },
    dismiss() {
      current = null
      notify()
    },
    getState() {
      return current
    },
    subscribe(listener) {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
  }
}

/**
 * Boot-time device capability probe.
 *
 * Fired before Start AR is offered, because a device whose ARCore is
 * missing never gets a session and would otherwise report NOTHING at
 * all — the exact blind spot behind the 2026-08-24 "certified device,
 * no planes" report. `isSessionSupported` is queried again here rather
 * than reusing the driver's call inside start(): this asks at a
 * different time for a different purpose, and the query is a cheap,
 * side-effect-free capability check.
 */
async function reportDeviceCapabilities(): Promise<Record<string, unknown>> {
  const xr = navigator.xr
  let immersiveArSupported = false
  if (xr != null) {
    try {
      immersiveArSupported = await xr.isSessionSupported('immersive-ar')
    } catch {
      immersiveArSupported = false
    }
  }
  let webgl2 = false
  try {
    webgl2 = document.createElement('canvas').getContext('webgl2') != null
  } catch {
    webgl2 = false
  }
  const nav = navigator as Navigator & {
    deviceMemory?: number
    hardwareConcurrency?: number
    userAgentData?: {
      getHighEntropyValues(hints: string[]): Promise<Record<string, string>>
    }
  }
  // The real device model and OS version. Chrome's User-Agent
  // Reduction freezes both in the UA string itself ("Android 10; K"),
  // so these hints are the only way to tell one Android phone from
  // another — see REDUCED_UA in ar-diagnostics.
  let highEntropy: { model?: string; platformVersion?: string } | undefined
  try {
    const values = await nav.userAgentData?.getHighEntropyValues([
      'model',
      'platformVersion',
    ])
    if (values != null) {
      highEntropy = {
        model: values.model,
        platformVersion: values.platformVersion,
      }
    }
  } catch {
    // Hints refused or unsupported — the probe falls back to the UA.
  }
  const probe = buildCapabilityProbe({
      userAgent: navigator.userAgent,
      highEntropy,
      webxrApiPresent: xr != null,
      immersiveArSupported,
      secureContext: window.isSecureContext === true,
      webgl2,
      deviceMemory: typeof nav.deviceMemory === 'number' ? nav.deviceMemory : null,
      hardwareConcurrency:
        typeof nav.hardwareConcurrency === 'number'
          ? nav.hardwareConcurrency
          : null,
      screenWidth: window.screen?.width ?? 0,
      screenHeight: window.screen?.height ?? 0,
      devicePixelRatio: window.devicePixelRatio ?? 1,
  })
  atoLog.event('ar_capability_probe', probe)
  return probe
}

/**
 * Reports errors nothing else catches.
 *
 * The per-site `atoLog.error` calls cover the failures we already knew
 * to look for. This is the net under everything else: a throw inside an
 * XR frame callback, a rejected promise in a load path, a runtime error
 * on a device whose browser we have never tested. On a single-screen AR
 * app those are usually terminal for the session, and without this they
 * are invisible unless someone has the phone on a USB cable.
 */
function installGlobalErrorReporting(): void {
  window.addEventListener('error', (e) => {
    atoLog.error('window_error', e.error ?? e.message, {
      error_source: `${e.filename ?? ''}:${e.lineno ?? 0}`,
    })
  })
  window.addEventListener('unhandledrejection', (e) => {
    atoLog.error('unhandled_rejection', e.reason)
  })
}

/** Correlates the start and summary posts for one session. randomUUID is not
 *  universal on older Android WebViews, so it degrades rather than throwing on
 *  the boot path. */
function newSessionId(): string {
  const uuid = globalThis.crypto?.randomUUID?.()
  if (uuid != null) return uuid.slice(0, 8)
  return Math.floor(Math.random() * 0xffffffff).toString(16)
}
